function createSearchDropdown(uniqueId) {
    return {
        isOpen: false,
        searchQuery: "",
        availableItems: true,
        soldItems: false,
        aiSearch: false,
        dropdownPosition: {},
        hasSearched: false,
        searchTimeout: null,
        isSearching: false,
        resultCount: 0,
        isDragging: false,

        getSearchText() {
            const reactiveValue = this.searchQuery?.trim() || '';
            if (reactiveValue) return reactiveValue;
            return this.$refs?.searchInput?.value?.trim() || '';
        },

        saveSearchToStorage() {
            siteStorage.set('search_dropdown_cache', {
                searchQuery: this.searchQuery,
                availableItems: this.availableItems,
                soldItems: this.soldItems,
                aiSearch: this.aiSearch,
                timestamp: Date.now()
            });
        },

        loadSearchFromStorage() {
            try {
                const searchState = siteStorage.get('search_dropdown_cache');
                if (searchState) {
                    this.searchQuery = searchState.searchQuery || '';
                    this.availableItems = searchState.availableItems ?? true;
                    this.soldItems = searchState.soldItems ?? false;
                    this.aiSearch = searchState.aiSearch ?? false;
                    return true;
                }
            } catch (e) {
                console.error('Error loading search from localStorage:', e);
                this.clearSearchStorage();
            }
            return false;
        },

        clearSearchStorage() {
            localStorage.removeItem('search_dropdown_cache');
        },

        clearSearch() {
            this.searchQuery = "";
            this.hasSearched = false;
            this.isSearching = false;
            this.resultCount = 0;
            const searchInput = this.$refs.searchInput;
            if (searchInput) searchInput.value = "";

            const resultsDiv = document.getElementById(`search-results-${uniqueId}`);
            if (resultsDiv) resultsDiv.innerHTML = "";

            // HTMX injects the count button but never removes it — reset manually on clear
            const countDiv = document.getElementById(`search-count-${uniqueId}`);
            if (countDiv) countDiv.innerHTML = "";

            this.clearSearchStorage();
        },

        handleClickOutside(event) {
            if (this.isDragging) return;
            this.isOpen = false;
        },

        handleCheckboxChange(type) {
            if (!this.availableItems && !this.soldItems) {
                if (type === 'available') this.availableItems = true;
                else this.soldItems = true;
            }
            if (this.searchQuery.length > 0) {
                this.isSearching = true;
                this.triggerSearch();
            }
        },

        triggerSearch() {
            const searchInput = this.$refs?.searchInput;
            if (searchInput && searchInput.value) this.searchQuery = searchInput.value;

            const searchText = this.getSearchText();
            if (searchText.length > 0) {
                const form = this.$refs.searchForm;
                const target = document.getElementById(`search-results-${uniqueId}`);

                if (form && target) {
                    this.hasSearched = true;
                    this.isSearching = true;
                    this.resultCount = 0;

                    if (this.$refs.workflowInput) this.$refs.workflowInput.value = this.aiSearch ? 'search_web_ai' : 'search_web';
                    if (this.$refs.searchTextInput) this.$refs.searchTextInput.value = searchText;
                    if (this.$refs.availableItemsInput) this.$refs.availableItemsInput.value = this.availableItems ? '1' : '0';
                    if (this.$refs.soldItemsInput) this.$refs.soldItemsInput.value = this.soldItems ? '1' : '0';

                    this.$nextTick(() => { htmx.trigger(form, "search-submit"); });
                } else {
                    console.error("Form or target not found:", { form: !!form, target: !!target });
                    if (!target) console.error(`Target element search-results-${uniqueId} not found in DOM`);
                }
            }
        },

        debounceSearch() {
            clearTimeout(this.searchTimeout);
            if (this.searchQuery.trim().length > 0) {
                this.searchTimeout = setTimeout(() => { this.triggerSearch(); }, 500);
            }
        },

        calculateDropdownPosition() {
            const isMobile = window.innerWidth < 768;
            const trigger = this.$el?.querySelector(".search-trigger");

            if (isMobile) {
                this.dropdownPosition = { position: "fixed", top: "0", left: "0", right: "0", width: "100%" };
                return;
            }

            if (!trigger) return;

            const dropdownWidth = 600;
            const padding = 16;
            const triggerRect = trigger.getBoundingClientRect();
            const viewportWidth = window.innerWidth;

            let rightOffset = viewportWidth - triggerRect.right;
            const dropdownLeftEdge = triggerRect.right - dropdownWidth;
            if (dropdownLeftEdge < padding) rightOffset = viewportWidth - dropdownWidth - padding;
            if (rightOffset < padding) rightOffset = padding;

            this.dropdownPosition = {
                position: "fixed",
                top: `${triggerRect.bottom + 8}px`,
                right: `${rightOffset}px`,
                left: "auto",
                width: `${Math.min(dropdownWidth, viewportWidth - (2 * padding))}px`
            };
        },

        init() {
            // Listen on document body for HTMX events since they bubble
            document.body.addEventListener('htmx:beforeRequest', (e) => {
                if (e.detail.elt === this.$refs.searchForm) {
                    this.hasSearched = true;
                    this.isSearching = true;
                }
            });
            document.body.addEventListener('htmx:afterSwap', (e) => {
                if (e.detail.elt === this.$refs.searchForm || e.detail.target.id === `search-results-${uniqueId}`) {
                    const items = e.detail.target.querySelectorAll('.group');
                    this.resultCount = items.length;
                    this.$nextTick(() => { this.saveSearchToStorage(); });
                }
            });
            document.body.addEventListener('htmx:afterRequest', (e) => {
                if (e.detail.elt === this.$refs.searchForm) this.isSearching = false;
            });
            document.body.addEventListener('htmx:responseError', (e) => {
                if (e.detail.elt === this.$refs.searchForm) {
                    console.error('htmx:responseError fired', e);
                    this.hasSearched = false;
                    this.isSearching = false;
                    console.error('Search error:', e.detail);
                }
            });

            this.$nextTick(() => this.calculateDropdownPosition());

            const searchInput = this.$refs.searchInput;
            if (searchInput) {
                searchInput.addEventListener('mousedown', () => { this.isDragging = false; });
                searchInput.addEventListener('mousemove', (e) => { if (e.buttons === 1) this.isDragging = true; });
                searchInput.addEventListener('mouseup', () => { setTimeout(() => { this.isDragging = false; }, 100); });
            }

            this.$watch("isOpen", (value) => {
                if (value) {
                    this.calculateDropdownPosition();
                    if (window.innerWidth < 768) document.body.style.overflow = "hidden";
                    this.$nextTick(() => {
                        const searchInput = this.$refs.searchInput;
                        if (searchInput) searchInput.focus();
                        if (this.loadSearchFromStorage() && this.searchQuery.length > 0) {
                            this.triggerSearch();
                        }
                    });
                } else {
                    document.body.style.overflow = "";
                }
            });

            this.$watch("aiSearch", () => {
                if (this.searchQuery.length > 0) {
                    this.isSearching = true;
                    this.triggerSearch();
                }
            });
        }
    };
}

function createSearchDropdownInline(uniqueId) {
    return {
        isOpen: false,
        searchQuery: "",
        availableItems: true,
        soldItems: false,
        aiSearch: false,
        hasSearched: false,
        searchTimeout: null,
        isSearching: false,
        resultCount: 0,
        isDragging: false,

        getSearchText() {
            const reactiveValue = this.searchQuery?.trim() || '';
            if (reactiveValue) return reactiveValue;
            return this.$refs?.searchInput?.value?.trim() || '';
        },

        saveSearchToStorage() {
            siteStorage.set('search_dropdown_cache', {
                searchQuery: this.searchQuery,
                availableItems: this.availableItems,
                soldItems: this.soldItems,
                aiSearch: this.aiSearch,
                timestamp: Date.now()
            });
        },

        loadSearchFromStorage() {
            try {
                const searchState = siteStorage.get('search_dropdown_cache');
                if (searchState) {
                    this.searchQuery = searchState.searchQuery || '';
                    this.availableItems = searchState.availableItems ?? true;
                    this.soldItems = searchState.soldItems ?? false;
                    this.aiSearch = searchState.aiSearch ?? false;
                    return true;
                }
            } catch (e) {
                console.error('Error loading search from localStorage:', e);
                this.clearSearchStorage();
            }
            return false;
        },

        clearSearchStorage() {
            localStorage.removeItem('search_dropdown_cache');
        },

        clearSearch() {
            this.searchQuery = "";
            this.hasSearched = false;
            this.isSearching = false;
            this.resultCount = 0;
            this.isOpen = false;
            const searchInput = this.$refs.searchInput;
            if (searchInput) searchInput.value = "";
            const resultsDiv = document.getElementById(`search-results-inline-${uniqueId}`);
            if (resultsDiv) resultsDiv.innerHTML = "";
            const countDiv = document.getElementById(`search-count-inline-${uniqueId}`);
            if (countDiv) countDiv.innerHTML = "";
            this.clearSearchStorage();
        },

        handleClickOutside(event) {
            if (this.isDragging) return;
            this.isOpen = false;
        },

        handleCheckboxChange(type) {
            if (!this.availableItems && !this.soldItems) {
                if (type === 'available') this.availableItems = true;
                else this.soldItems = true;
            }
            if (this.searchQuery.length > 0) {
                this.isSearching = true;
                this.triggerSearch();
            }
        },

        triggerSearch() {
            const searchInput = this.$refs?.searchInput;
            if (searchInput && searchInput.value) this.searchQuery = searchInput.value;
            const searchText = this.getSearchText();
            if (searchText.length > 0) {
                const form = this.$refs.searchForm;
                const target = document.getElementById(`search-results-inline-${uniqueId}`);
                if (form && target) {
                    this.hasSearched = true;
                    this.isOpen = true;
                    this.isSearching = true;
                    this.resultCount = 0;
                    if (this.$refs.workflowInput) this.$refs.workflowInput.value = this.aiSearch ? 'search_web_ai' : 'search_web';
                    if (this.$refs.searchTextInput) this.$refs.searchTextInput.value = searchText;
                    if (this.$refs.availableItemsInput) this.$refs.availableItemsInput.value = this.availableItems ? '1' : '0';
                    if (this.$refs.soldItemsInput) this.$refs.soldItemsInput.value = this.soldItems ? '1' : '0';
                    this.$nextTick(() => { htmx.trigger(form, "search-submit"); });
                }
            }
        },

        debounceSearch() {
            clearTimeout(this.searchTimeout);
            if (this.searchQuery.trim().length > 0) {
                this.isOpen = true;
                this.searchTimeout = setTimeout(() => { this.triggerSearch(); }, 500);
            } else {
                this.isOpen = false;
            }
        },

        init() {
            document.body.addEventListener('htmx:beforeRequest', (e) => {
                if (e.detail.elt === this.$refs.searchForm) {
                    this.hasSearched = true;
                    this.isSearching = true;
                }
            });
            document.body.addEventListener('htmx:afterSwap', (e) => {
                if (e.detail.target.id === `search-results-inline-${uniqueId}`) {
                    const items = e.detail.target.querySelectorAll('.group');
                    this.resultCount = items.length;
                    this.$nextTick(() => { this.saveSearchToStorage(); });
                }
            });
            document.body.addEventListener('htmx:afterRequest', (e) => {
                if (e.detail.elt === this.$refs.searchForm) this.isSearching = false;
            });
            document.body.addEventListener('htmx:responseError', (e) => {
                if (e.detail.elt === this.$refs.searchForm) {
                    this.hasSearched = false;
                    this.isSearching = false;
                }
            });

            // Track dragging so click-outside doesn't fire on text selection
            const searchInput = this.$refs.searchInput;
            if (searchInput) {
                searchInput.addEventListener('mousedown', () => { this.isDragging = false; });
                searchInput.addEventListener('mousemove', (e) => { if (e.buttons === 1) this.isDragging = true; });
                searchInput.addEventListener('mouseup', () => { setTimeout(() => { this.isDragging = false; }, 100); });
            }
        }
    };
}

window.createSearchDropdown = createSearchDropdown;
window.createSearchDropdownInline = createSearchDropdownInline;
